/**
 * CrewReportCardDialog — 2026-02-26
 * -------------------------------------------------------------------------
 * Manager-only pop-out that answers "how's this crew member doing today?".
 * Opened by tapping a crew name on the Home "On the floor" dialog or the
 * Zone Sheet page.
 *
 * Sections:
 *   1. Header — name, role chip, home store, visiting pill.
 *   2. Shift — today's shift + break + walkie/zebra + visitor status.
 *   3. Tasks — coverage_tasks with live status chips + optional reassign.
 *   4. Goal  — weekly captures vs weekly_goal + WTD rank at their store.
 *   5. Notes — manager-only notes; add + delete inline.
 *
 * The dialog is READ-ONLY except for:
 *   - adding / deleting manager notes
 *   - reassigning a coverage task (managers only)
 * per the user's explicit scoping choice.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogTitle,
} from "./ui/dialog";
import {
  Clock, ListChecks, Trophy, ChatCircleDots, X, Trash, PaperPlaneTilt,
  Sparkle, MapPin, Warning, CheckCircle, CalendarX, Plus,
} from "@phosphor-icons/react";
import AttendanceQuickLog from "./AttendanceQuickLog";
import AttendanceDetailDialog, { ThresholdChip } from "./AttendanceDetailDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map coverage_task.status → chip label + tailwind classes.
const STATUS_CHIP = {
  not_acknowledged: { label: "New", cls: "bg-slate-100 text-slate-600" },
  acknowledged:     { label: "Got it", cls: "bg-amber-100 text-amber-800" },
  in_progress:      { label: "In progress", cls: "bg-blue-100 text-blue-800" },
  completed:        { label: "Done", cls: "bg-emerald-100 text-emerald-800" },
  manager_verified: { label: "Verified", cls: "bg-emerald-200 text-emerald-900" },
  needs_attention:  { label: "Needs attn", cls: "bg-rose-100 text-rose-800" },
  needs_handoff:    { label: "Handoff", cls: "bg-rose-100 text-rose-800" },
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    if (isToday) return time;
    return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${time}`;
  } catch {
    return "";
  }
};

export default function CrewReportCardDialog({
  open,
  onOpenChange,
  crewId,
  actorId,
  date,           // YYYY-MM-DD (optional — defaults server-side to today)
  storeId,        // optional — hint for which store's sheet to look at
  onReassign,     // optional callback (task, crewId) → parent handles the flow
  scrollToAttendance = false,  // when true, scroll to the attendance section on open (called-out chip)
}) {
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  // Attendance snapshot lives beside the main card fetch — separate
  // endpoint so a partial failure on one doesn't block the other.
  const [attendance, setAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const attendanceRef = useRef(null);

  const loadAttendance = useCallback(async () => {
    if (!crewId || !actorId) return;
    setAttendanceLoading(true);
    try {
      const { data } = await axios.get(`${API}/crew/${crewId}/attendance`, { params: { actor_id: actorId } });
      setAttendance(data);
    } catch {
      setAttendance(null);
    } finally {
      setAttendanceLoading(false);
    }
  }, [crewId, actorId]);

  const load = useCallback(async () => {
    if (!crewId || !actorId) return;
    setLoading(true);
    setError("");
    try {
      const params = { actor_id: actorId };
      if (date) params.date = date;
      if (storeId) params.store_id = storeId;
      const { data } = await axios.get(`${API}/crew/${crewId}/report-card`, { params });
      setCard(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't load report card.";
      setError(String(msg));
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [crewId, actorId, date, storeId]);

  useEffect(() => {
    if (open && crewId) {
      load();
      loadAttendance();
    } else if (!open) {
      // Reset local state so the next open is a fresh card.
      setCard(null);
      setAttendance(null);
      setNoteDraft("");
      setError("");
    }
  }, [open, crewId, load, loadAttendance]);

  // When opened from a "CALLED OUT" chip, jump straight to the
  // attendance section once the card + snapshot have rendered.
  useEffect(() => {
    if (open && scrollToAttendance && !loading && !attendanceLoading && attendanceRef.current) {
      const t = setTimeout(() => {
        try { attendanceRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* noop */ }
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open, scrollToAttendance, loading, attendanceLoading]);

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setPosting(true);
    try {
      const { data } = await axios.post(
        `${API}/crew/${crewId}/notes`,
        { actor_id: actorId, text },
      );
      setCard((c) => c ? { ...c, notes: [data, ...(c.notes || [])] } : c);
      setNoteDraft("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save note.");
    } finally {
      setPosting(false);
    }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/crew/${crewId}/notes/${noteId}`, {
        params: { actor_id: actorId },
      });
      setCard((c) => c ? { ...c, notes: (c.notes || []).filter((n) => n.id !== noteId) } : c);
      toast.success("Note deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete note.");
    }
  };

  const crew = card?.crew;
  const shift = card?.shift;
  const tasks = card?.tasks;
  const weekly = card?.weekly;
  const notes = card?.notes || [];
  const displayName = crew ? [crew.first_name, crew.last_name].filter(Boolean).join(" ") || "Crew" : "Crew";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-md w-[calc(100%-16px)] max-h-[88vh] overflow-hidden rounded-3xl bg-white"
        data-testid="crew-report-card-dialog"
      >
        <DialogTitle className="sr-only">Report card for {displayName}</DialogTitle>

        {loading && (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="crew-report-card-loading">
            Loading report card…
          </div>
        )}

        {!loading && error && (
          <div className="p-6" data-testid="crew-report-card-error">
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-2">
              <Warning weight="fill" className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-black text-rose-900">Couldn&apos;t load report card</p>
                <p className="text-xs text-rose-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && card && (
          <div className="flex flex-col max-h-[88vh]">
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-black text-lg flex-shrink-0 shadow-sm">
                  {crew?.photo_url ? (
                    <img src={crew.photo_url} alt={displayName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    (crew?.first_name?.[0] || "?").toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-lg font-black text-slate-900 leading-tight truncate" data-testid="crew-report-card-name">
                      {displayName}
                    </p>
                    {shift?.is_visitor && shift?.home_store_code && (
                      <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Visiting {shift.home_store_code}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <span className="font-semibold">{crew?.role_label}</span>
                    {crew?.home_store_code && (
                      <>
                        <span className="text-slate-300">·</span>
                        <MapPin weight="fill" className="w-3 h-3" />
                        <span>Store {crew.home_store_code}</span>
                      </>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="text-slate-400 hover:text-slate-600 p-1 -mr-1 flex-shrink-0"
                  aria-label="Close"
                  data-testid="crew-report-card-close"
                >
                  <X weight="bold" className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
              {/* Shift section */}
              <section className="px-5 py-4" data-testid="crew-report-card-shift">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Clock weight="fill" className="w-3.5 h-3.5" /> Today&apos;s shift
                </p>
                {shift?.assigned_today ? (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Shift</p>
                      <p className="text-sm font-black text-slate-900 mt-0.5">{shift.shift || "—"}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Break</p>
                      <p className="text-sm font-black text-slate-900 mt-0.5">{shift.break_time || "—"}</p>
                    </div>
                    <div className="col-span-2 flex items-center gap-3 text-[11px] text-slate-600">
                      <span className={`inline-flex items-center gap-1 ${shift.walkie ? "text-emerald-700" : "text-slate-400"}`}>
                        {shift.walkie ? <CheckCircle weight="fill" className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-slate-300 inline-block" />}
                        Walkie
                      </span>
                      <span className={`inline-flex items-center gap-1 ${shift.zebra ? "text-emerald-700" : "text-slate-400"}`}>
                        {shift.zebra ? <CheckCircle weight="fill" className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-slate-300 inline-block" />}
                        Zebra
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-500 mt-1 italic" data-testid="crew-report-card-shift-empty">
                    Not on today&apos;s zone sheet.
                  </p>
                )}
              </section>

              {/* Tasks section */}
              <section className="px-5 py-4" data-testid="crew-report-card-tasks">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                    <ListChecks weight="fill" className="w-3.5 h-3.5" /> Task progress
                  </p>
                  {tasks?.total > 0 && (
                    <p className="text-[11px] text-slate-500 font-bold">
                      {tasks.done}/{tasks.total} done
                    </p>
                  )}
                </div>
                {tasks?.total ? (
                  <ul className="mt-2 space-y-1.5">
                    {tasks.items.map((t) => {
                      const chip = STATUS_CHIP[t.status] || { label: t.status || "—", cls: "bg-slate-100 text-slate-600" };
                      return (
                        <li
                          key={t.id}
                          className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
                          data-testid={`crew-report-card-task-${t.id}`}
                        >
                          <span className="text-[13px] font-semibold text-slate-800 truncate flex-1">
                            {t.label}
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className={`text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded ${chip.cls}`}>
                              {chip.label}
                            </span>
                            {onReassign && shift?.assigned_today && (
                              <button
                                type="button"
                                onClick={() => onReassign(t, crewId)}
                                className="text-[10px] font-bold text-blue-600 hover:text-blue-800 px-1"
                                data-testid={`crew-report-card-task-${t.id}-reassign`}
                              >
                                Reassign
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-[13px] text-slate-500 mt-1 italic">No coverage tasks assigned.</p>
                )}
              </section>

              {/* Weekly goal section */}
              <section className="px-5 py-4" data-testid="crew-report-card-weekly">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Trophy weight="fill" className="w-3.5 h-3.5" /> Weekly goal
                </p>
                <div className="mt-2">
                  {weekly?.goal > 0 ? (
                    <>
                      <div className="flex items-baseline justify-between">
                        <p className="text-2xl font-black text-slate-900">
                          {weekly.captures} <span className="text-sm text-slate-400 font-bold">/ {weekly.goal}</span>
                        </p>
                        <p className="text-[11px] text-slate-500 font-bold">
                          {weekly.progress_pct != null ? `${weekly.progress_pct}%` : "—"}
                        </p>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all"
                          style={{ width: `${Math.min(100, weekly.progress_pct || 0)}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-800">
                      <span className="text-2xl font-black text-slate-900">{weekly?.captures ?? 0}</span>
                      <span className="text-xs text-slate-500 font-bold ml-1">captures this week</span>
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                    <span>{weekly?.days_active || 0} day{(weekly?.days_active || 0) === 1 ? "" : "s"} active</span>
                    {weekly?.rank && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1 font-bold text-amber-700">
                          <Sparkle weight="fill" className="w-3 h-3" /> #{weekly.rank} at store
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </section>

              {/* Attendance section — 6-mo rolling snapshot per Five
                  Below policy. Managers can log a new event inline
                  (opens AttendanceQuickLog) or tap "See all
                  occurrences" for the detail popout the user
                  explicitly requested 2026-02-26. */}
              <section className="px-5 py-4" data-testid="crew-report-card-attendance" ref={attendanceRef}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                    <CalendarX weight="fill" className="w-3.5 h-3.5" /> Attendance · 6mo
                  </p>
                  <button
                    type="button"
                    onClick={() => setLogOpen(true)}
                    className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1"
                    data-testid="crew-report-card-attendance-log-btn"
                  >
                    <Plus weight="bold" className="w-3 h-3" /> Log
                  </button>
                </div>
                {attendanceLoading && (
                  <p className="text-[13px] text-slate-500 mt-1 italic">Loading…</p>
                )}
                {!attendanceLoading && attendance && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ThresholdChip snapshot={attendance.snapshot} />
                      {attendance.snapshot?.next_threshold && (
                        <span className="text-[11px] text-slate-500">
                          {attendance.snapshot.next_threshold.occ - attendance.snapshot.occurrences} away from {attendance.snapshot.next_threshold.label}
                        </span>
                      )}
                    </div>
                    {(attendance.counts?.by_type && Object.keys(attendance.counts.by_type).length > 0) && (
                      <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-slate-500">
                        {Object.entries(attendance.counts.by_type).map(([k, v]) => (
                          <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100">
                            {k.replace("_", " ")} · {v}
                          </span>
                        ))}
                      </div>
                    )}
                    {(attendance.events || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => setDetailOpen(true)}
                        className="text-[11px] font-black text-blue-700 hover:text-blue-900 hover:underline"
                        data-testid="crew-report-card-attendance-see-all"
                      >
                        See all {attendance.events.length} occurrence{attendance.events.length === 1 ? "" : "s"} →
                      </button>
                    )}
                    {(attendance.events || []).length === 0 && (
                      <p className="text-[13px] text-slate-500 italic">Clean sheet — no events in window.</p>
                    )}
                  </div>
                )}
                {!attendanceLoading && !attendance && (
                  <p className="text-[13px] text-slate-500 mt-1 italic">Couldn&apos;t load attendance.</p>
                )}
              </section>

              {/* Notes section */}
              <section className="px-5 py-4" data-testid="crew-report-card-notes">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <ChatCircleDots weight="fill" className="w-3.5 h-3.5" /> Manager notes
                </p>
                {notes.length === 0 ? (
                  <p className="text-[13px] text-slate-500 mt-1 italic">No notes yet.</p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {notes.map((n) => (
                      <li
                        key={n.id}
                        className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2"
                        data-testid={`crew-report-card-note-${n.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[13px] text-slate-800 leading-snug whitespace-pre-wrap break-words flex-1">
                            {n.text}
                          </p>
                          {n.author_id === actorId && (
                            <button
                              type="button"
                              onClick={() => removeNote(n.id)}
                              className="text-slate-400 hover:text-rose-500 p-0.5 flex-shrink-0"
                              aria-label="Delete note"
                              data-testid={`crew-report-card-note-${n.id}-delete`}
                            >
                              <Trash weight="bold" className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 font-semibold">
                          {n.author_name}{n.created_at ? ` · ${fmtTime(n.created_at)}` : ""}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {/* Add note composer */}
                <div className="mt-3 flex items-end gap-2">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Leave a note for this crew member…"
                    rows={2}
                    className="flex-1 text-sm rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 px-3 py-2 resize-none"
                    maxLength={1000}
                    data-testid="crew-report-card-note-input"
                  />
                  <button
                    type="button"
                    onClick={addNote}
                    disabled={posting || !noteDraft.trim()}
                    className="rounded-lg bg-blue-600 text-white px-3 py-2 disabled:opacity-40 hover:bg-blue-700 transition-colors flex items-center gap-1 text-xs font-black flex-shrink-0"
                    data-testid="crew-report-card-note-submit"
                  >
                    <PaperPlaneTilt weight="fill" className="w-3.5 h-3.5" /> Send
                  </button>
                </div>
              </section>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Nested manager surfaces — mounted at the same Dialog root so
          Shadcn's overlay z-index resolves correctly. Both are gated
          by their own `open` state, so they don't render until asked. */}
      <AttendanceQuickLog
        open={logOpen}
        onOpenChange={setLogOpen}
        crewId={crewId}
        crewName={displayName}
        actorId={actorId}
        onLogged={() => loadAttendance()}
      />
      <AttendanceDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        crewId={crewId}
        crewName={displayName}
        actorId={actorId}
        onChanged={() => loadAttendance()}
      />
    </Dialog>
  );
}
